import { Avatar, Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useEffect, useState } from "react";

import { apiBlob } from "../lib/api.js";
import { PresenceDot, type PresenceState } from "./PresenceDot.js";

/** Up-to-two-letter initials from a display name (first + last word). */
export function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

// ─────────────────────────────────────────────────────────────────────────
// Shared avatar image cache. A person's avatar is the SAME everywhere, so we
// fetch it once per session (auth-gated `GET /api/users/:id/avatar`, served
// same-origin from portal-api) and share the resulting object URL across
// every <UserAvatar> for that user — the roster of N people = N fetches, not
// N×renders. `apiBlob` carries the bearer + uses the absolute dashboard
// origin on the Tauri desktop shell, which a raw `<img src>` could not. A
// missing avatar (404) resolves to `null` → the initials fallback renders.
// `bustAvatar(userId)` clears the entry + notifies live instances after an
// upload so the new photo appears without a reload.
// ─────────────────────────────────────────────────────────────────────────
const avatarCache = new Map<number, Promise<string | null>>();
const listeners = new Set<(userId: number) => void>();

function loadAvatar(userId: number): Promise<string | null> {
  let p = avatarCache.get(userId);
  if (!p) {
    p = apiBlob(`/api/users/${userId}/avatar`)
      .then((blob) => (blob.size > 0 ? URL.createObjectURL(blob) : null))
      .catch(() => null);
    avatarCache.set(userId, p);
  }
  return p;
}

/** Drop a user's cached avatar + tell every live <UserAvatar> to re-fetch.
 * Call after an upload (self or admin-set) so the new photo shows at once. */
export function bustAvatar(userId: number): void {
  const prev = avatarCache.get(userId);
  if (prev) {
    void prev.then((url) => {
      if (url) URL.revokeObjectURL(url);
    });
  }
  avatarCache.delete(userId);
  for (const l of listeners) l(userId);
}

/**
 * A round avatar that shows the user's uploaded photo when they have one,
 * falling back to up-to-two-letter initials on a deterministic, theme-derived
 * colour (stable per user id) with an optional presence dot. Used in the chat
 * roster, conversation list, header, the global presence rail, and the User
 * Management list, so a person looks identical everywhere.
 *
 * Colours are pulled from the MUI palette (brand + per-system accents), never
 * raw hex — so the avatar set re-tints with the theme and respects dark mode.
 */
export function UserAvatar({
  name,
  userId,
  size = 36,
  presence,
}: {
  name: string;
  userId: number;
  size?: number;
  presence?: PresenceState;
}): JSX.Element {
  const theme = useTheme();
  // Per-system accents come from the custom theme; guard each access so a
  // bare default MUI theme (e.g. a unit test that doesn't wrap in the app
  // ThemeProvider) falls back to the brand colours instead of crashing.
  const sys = theme.palette.system as Partial<Record<string, string>> | undefined;
  const palette = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.info.main,
    theme.palette.warning.main,
    sys?.support,
    sys?.crm,
    sys?.sales,
    sys?.docs,
    sys?.marketing,
  ].filter((c): c is string => typeof c === "string");
  const bg =
    palette[Math.abs(Math.trunc(userId)) % palette.length] ?? theme.palette.primary.main;
  const dotSize = Math.max(8, Math.round(size * 0.3));

  // Resolve the avatar image (cached + shared). Re-runs when bustAvatar()
  // fires for this user. A real, positive user id is required — some hand
  // -rolled callers pass 0/NaN for synthetic rows; those stay initials-only.
  const [imgSrc, setImgSrc] = useState<string | null>(null);
  const hasRealId = Number.isInteger(userId) && userId > 0;
  useEffect(() => {
    if (!hasRealId) {
      setImgSrc(null);
      return;
    }
    let alive = true;
    const apply = (): void => {
      void loadAvatar(userId).then((url) => {
        if (alive) setImgSrc(url);
      });
    };
    apply();
    const onBust = (id: number): void => {
      if (id === userId) apply();
    };
    listeners.add(onBust);
    return () => {
      alive = false;
      listeners.delete(onBust);
    };
  }, [userId, hasRealId]);

  return (
    <Box sx={{ position: "relative", display: "inline-flex", flexShrink: 0, lineHeight: 0 }}>
      <Avatar
        // When src is set MUI renders the image and falls back to the
        // initials children if it fails to load; with no src the initials
        // render on the deterministic colour. `src` is spread conditionally
        // (not `src={x ?? undefined}`) for exactOptionalPropertyTypes.
        {...(imgSrc ? { src: imgSrc } : {})}
        alt={name}
        sx={{
          width: size,
          height: size,
          bgcolor: bg,
          color: theme.palette.getContrastText(bg),
          fontSize: Math.round(size * 0.42),
          fontWeight: 600,
        }}
      >
        {initialsFor(name)}
      </Avatar>
      {presence && (
        <Box sx={{ position: "absolute", right: -1, bottom: -1 }}>
          <PresenceDot state={presence} size={dotSize} ring />
        </Box>
      )}
    </Box>
  );
}
